import React from "react";
import { CubeIcon } from "@heroicons/react/24/outline";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

/**
 * Preview components that mirror the exact look of the public Next.js site.
 * Keep the JSX and Tailwind classes in sync with:
 *   homesite/venueboard-main/src/components/blog/{BlogCard,BlogPostView,MarkdownImage,TagChips}.js
 *   homesite/venueboard-main/src/components/blog/VenueEmbed.jsx
 *   homesite/venueboard-main/src/lib/venueEmbeds.js
 *   homesite/venueboard-main/src/app/blog/[slug]/page.js
 *
 * Used only inside the admin BlogPreview modal.
 */

// --- Markdown image with size hints (mirror of public MarkdownImage.js) ------

const SIZE_CLASSES = {
  small: "max-w-xs mx-auto",
  medium: "max-w-md mx-auto",
  large: "max-w-2xl mx-auto",
  full: "w-full",
};

export function PreviewMarkdownImage({ src, alt }) {
  const rawAlt = alt || "";
  const parts = rawAlt.split("|").map((s) => s.trim());
  const caption = parts[0] || "";
  const sizeKey = (parts[1] || "full").toLowerCase();
  const sizeClass = SIZE_CLASSES[sizeKey] || SIZE_CLASSES.full;
  return (
    <figure className={`my-6 ${sizeClass}`}>
      <img
        src={src}
        alt={caption}
        loading="lazy"
        className="h-auto w-full rounded-lg shadow-sm"
      />
      {caption && (
        <figcaption className="mt-2 text-center text-sm text-gray-500">
          {caption}
        </figcaption>
      )}
    </figure>
  );
}

// --- Venues embebidos (mirror de lib/venueEmbeds.js + VenueEmbed.jsx) --------

// Mismo parseo que el homesite: una línea por venue, `id | nota` opcional,
// `#` comenta. El orden del bloque es la curaduría del autor.
export function parsePreviewVenueBlock(body = "") {
  return String(body)
    .split("\n")
    .map((l) => l.trim())
    .filter((l) => l && !l.startsWith("#"))
    .map((l) => {
      const [rawId, ...rest] = l.split("|");
      const id = rawId.trim();
      const note = rest.join("|").trim();
      return id ? { id, note: note || null } : null;
    })
    .filter(Boolean);
}

export function PreviewVenueEmbed({ refs = [], venuesById = {}, locale = "es" }) {
  const upTo = (n) => (locale === "en" ? `Up to ${n} guests` : `Hasta ${n} invitados`);
  const items = refs.map((r) => ({ venue: venuesById[r.id], ...r }));
  const missing = items.filter((i) => !i.venue).map((i) => i.id);
  const found = items.filter((i) => i.venue);

  return (
    <div className="not-prose my-10">
      {/* El preview SÍ avisa de los ids que no existen; el sitio público los
          omite en silencio. Es el único lugar donde el autor puede corregirlos. */}
      {missing.length > 0 && (
        <p className="mb-3 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800">
          Estos ids no existen o no están publicados y no se van a mostrar en el
          sitio: <strong>{missing.join(", ")}</strong>
        </p>
      )}
      <div className={`grid gap-6 ${found.length === 1 ? "grid-cols-1 max-w-md" : "grid-cols-1 sm:grid-cols-2"}`}>
        {found.map(({ venue, note }) => (
          <div
            key={venue.id}
            className="group flex flex-col overflow-hidden rounded-xl border border-gray-100 bg-white shadow-md"
          >
            {venue.images?.[0] && (
              <div className="relative h-44 w-full overflow-hidden bg-gray-100">
                <img
                  src={venue.images[0]}
                  alt={venue.name}
                  className="absolute inset-0 h-full w-full object-cover object-center"
                />
              </div>
            )}
            <div className="flex flex-1 flex-col p-5">
              <h3 className="mb-2 text-lg font-semibold text-gray-900">{venue.name}</h3>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-600">
                {venue.zone && <span>{venue.zone}</span>}
                {venue.capacity && <span>{upTo(venue.capacity)}</span>}
                {/* Mismo badge que VenuesCardList y el VenueEmbed público. */}
                {venue.hasRecorrido && (
                  <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-[#1A4D2E] px-2 py-0.5 text-xs font-medium text-white shadow-sm">
                    <CubeIcon className="h-3 w-3 flex-shrink-0" />
                    3D
                  </span>
                )}
              </div>
              {Array.isArray(venue.type) && venue.type.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {venue.type.slice(0, 3).map((tipo) => (
                    <span
                      key={tipo}
                      className="rounded-full bg-[#1A4D2E]/10 px-3 py-1 text-xs text-[#1A4D2E]"
                    >
                      {tipo}
                    </span>
                  ))}
                </div>
              )}
              {note && (
                <p className="mt-3 border-l-2 border-[#1A4D2E]/30 pl-3 text-sm italic text-gray-500">
                  {note}
                </p>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// --- Paragraph unwrap (mirror of BlogPostView.js Paragraph override) ---------

export function PreviewParagraph({ node, children, ...props }) {
  const kids = node?.children || [];
  const isImageOnly =
    kids.length > 0 &&
    kids.every(
      (child) =>
        (child.type === "element" && child.tagName === "img") ||
        (child.type === "text" && /^\s*$/.test(child.value || ""))
    );
  if (isImageOnly) return <>{children}</>;
  return <p {...props}>{children}</p>;
}

function previewCodeText(node, children) {
  const fromNode = (node?.children || [])
    .filter((c) => c.type === "text")
    .map((c) => c.value)
    .join("");
  if (fromNode) return fromNode;
  return React.Children.toArray(children).filter((c) => typeof c === "string").join("");
}

// Factory: los componentes necesitan venuesById/locale, que cambian por render.
export function previewMdComponents({ venuesById = {}, locale = "es" } = {}) {
  return {
    img: PreviewMarkdownImage,
    p: PreviewParagraph,
    code: function PreviewCode({ node, className, children, ...props }) {
      if (className === "language-venues") {
        return (
          <PreviewVenueEmbed
            refs={parsePreviewVenueBlock(previewCodeText(node, children))}
            venuesById={venuesById}
            locale={locale}
          />
        );
      }
      return (
        <code className={className} {...props}>
          {children}
        </code>
      );
    },
    pre: function PreviewPre({ node, children, ...props }) {
      const only = node?.children?.length === 1 ? node.children[0] : null;
      const isVenueBlock =
        only?.tagName === "code" &&
        (only.properties?.className || []).includes("language-venues");
      if (isVenueBlock) return <>{children}</>;
      return <pre {...props}>{children}</pre>;
    },
  };
}

// --- Helpers -----------------------------------------------------------------

function formatDate(date, locale) {
  if (!date) return "";
  return date.toLocaleDateString(locale === "en" ? "en-US" : "es-MX", {
    // Zona fija: el post se fecha en hora de México. Sin esto el servidor
    // formatea en UTC y el navegador en la zona del visitante, así que un
    // publishedAt cercano a medianoche muestra días distintos en cada uno
    // (y en un client component eso es hydration mismatch).
    timeZone: "America/Mexico_City",
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

function labelForTag(tagSlug, allTags, locale) {
  const tag = allTags.find((t) => t.slug === tagSlug);
  if (!tag) return tagSlug;
  return (tag.labels && (tag.labels[locale] || tag.labels.es)) || tagSlug;
}

// --- Card preview (mirror of BlogCard.js) ------------------------------------

export function PreviewBlogCard({ post, locale, allTags }) {
  const title =
    post.translations?.[locale]?.title ||
    post.translations?.es?.title ||
    "Sin título";
  const excerpt = post.translations?.[locale]?.excerpt || "";
  const tagSlugs = (post.tags || []).slice(0, 3);
  const dateLabel = formatDate(new Date(), locale);
  const readMore = locale === "en" ? "Read more" : "Leer más";

  return (
    <div className="group flex flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm transition hover:shadow-md hover:border-[#1A4D2E]/30 max-w-sm mx-auto cursor-pointer">
      {post.coverImage ? (
        <div className="relative block h-56 w-full overflow-hidden">
          <img
            src={post.coverImage}
            alt={title}
            className="absolute inset-0 h-full w-full object-cover"
          />
        </div>
      ) : (
        <div className="flex h-56 w-full items-center justify-center bg-gray-100 text-sm text-gray-400">
          (Sin portada)
        </div>
      )}
      <div className="flex flex-1 flex-col p-6">
        {tagSlugs.length > 0 && (
          <div className="mb-3 flex flex-wrap gap-1">
            {tagSlugs.map((slug) => (
              <span
                key={slug}
                className="rounded-full bg-[#1A4D2E]/10 px-2 py-0.5 text-xs text-[#1A4D2E]"
              >
                {labelForTag(slug, allTags, locale)}
              </span>
            ))}
          </div>
        )}
        <h2 className="mb-2 text-xl font-bold text-[#2D2D2D] transition-colors group-hover:text-[#1A4D2E]">
          {title}
        </h2>
        {excerpt && (
          <p className="mb-4 flex-1 text-sm text-gray-600 line-clamp-3">
            {excerpt}
          </p>
        )}
        <div className="mt-auto flex items-center justify-between text-xs text-gray-500">
          <span>{dateLabel}</span>
          <span className="font-medium text-[#1A4D2E] group-hover:underline">
            {readMore} →
          </span>
        </div>
      </div>
    </div>
  );
}

// --- Full post preview (mirror of /blog/[slug]/page.js body) -----------------

export function PreviewBlogPost({ post, locale, allTags, authorName, venuesById = {} }) {
  const t = post.translations?.[locale];
  if (!t) {
    return (
      <div className="p-12 text-center text-gray-500">
        No hay traducción en {locale === "en" ? "Inglés" : "Español"}.
      </div>
    );
  }
  const title = t.title || "Sin título";
  const excerpt = t.excerpt || "";
  const body = t.body || "";
  const tagSlugs = post.tags || [];
  const backLabel = locale === "en" ? "← Back to blog" : "← Volver al blog";
  const byLabel = locale === "en" ? "By" : "Por";
  const publishedLabel = locale === "en" ? "Published on" : "Publicado el";

  return (
    <div className="bg-white">
      {/* Cover hero — mirrors /blog/[slug]/page.js */}
      {post.coverImage && (
        <div className="relative h-64 w-full overflow-hidden bg-gray-100">
          <img
            src={post.coverImage}
            alt={title}
            className="absolute inset-0 h-full w-full object-cover object-center"
          />
        </div>
      )}

      {/* Header section */}
      <section className="relative overflow-hidden">
        <div className="relative z-10 mx-auto max-w-3xl px-6 pt-8">
          <span className="mb-6 inline-flex items-center gap-1 text-sm text-[#1A4D2E]">
            {backLabel}
          </span>

          {tagSlugs.length > 0 && (
            <div className="mb-4 flex flex-wrap gap-2">
              {tagSlugs.map((slug) => (
                <span
                  key={slug}
                  className="rounded-full bg-[#1A4D2E]/10 px-3 py-1 text-xs text-[#1A4D2E]"
                >
                  {labelForTag(slug, allTags, locale)}
                </span>
              ))}
            </div>
          )}

          <h1 className="mb-4 text-3xl font-bold text-[#2D2D2D] md:text-4xl">
            {title}
          </h1>

          <div className="mb-10 flex flex-wrap items-center gap-3 text-sm text-gray-500">
            {authorName && (
              <span>
                {byLabel} <strong>{authorName}</strong>
              </span>
            )}
            <span>•</span>
            <time>
              {publishedLabel} {formatDate(new Date(), locale)}
            </time>
          </div>

          {excerpt && (
            <p className="mb-6 text-lg italic text-gray-500 border-l-4 border-[#1A4D2E]/30 pl-4">
              {excerpt}
            </p>
          )}
        </div>
      </section>

      {/* Article body */}
      <div className="mx-auto max-w-3xl px-6 pb-16">
        {body.trim() ? (
          <article className="prose prose-lg max-w-none">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={previewMdComponents({ venuesById, locale })}
            >
              {body}
            </ReactMarkdown>
          </article>
        ) : (
          <p className="text-gray-400">(Sin contenido todavía.)</p>
        )}
      </div>
    </div>
  );
}
